import React, { useEffect, useState } from 'react';
import { Grid, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { CalculatorType } from 'spw-redux/modules/calculators/types';
import { IProject } from 'spw-redux/modules/projects/types';
import { colors } from 'spw-theme';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import {
  formatBigNumber,
  handleOnKeyPress,
  limitToCharLength,
} from 'spw-shared/utils';
import { MAX_MACRO_HEADCOUNT } from 'spw-constants';
import { onlyAllowNumbersFromInput } from 'spw-components/worksheet/utils';
import { actions } from 'spw-redux/modules/calculators/actions';
import { ScenarioHeader } from 'spw-components/downloads/image-header';
import DeskSharingGuide from './desk-sharing-guide';
import HeadcountUtilizationGuide from './headcount-utilization-guide';
import Headcount from './headcount';
import {
  Container,
  Content,
  FormTitle,
  HeadcountInput,
  InputWrapper,
  Label,
  NumberOp,
  Operator,
} from './styles';
import { PlainProgramScenario } from '../../models/Scenario';
import { formatWithDecimals } from './utils';

export interface IProps {
  scenario: PlainProgramScenario;
  isEditingSavedScenario: boolean;
  calculatorType: CalculatorType;
  project: IProject;
  showDecimals: boolean;
  calculatorActions?: JSX.Element;
  disableHighlighting?: boolean;
  isRenderingForExport?: boolean;
}

export const MacroProgramming = ({
  scenario,
  isEditingSavedScenario,
  calculatorType,
  showDecimals,
  calculatorActions,
  isRenderingForExport,
  disableHighlighting = false,
}: IProps) => {
  const dispatch = useDispatch();
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);

  const [base, setBase] = useState(formatBigNumber(scenario?.headcount || 0));
  const [isBaseDirty, setIsBaseDirty] = useState(parseFloat(base) > 0);

  const handleBaseInputFocus = () => {
    setBase(formatBigNumber(scenario?.headcount || 0));
  };

  useEffect(() => {
    setBase(formatBigNumber(scenario?.headcount || 0));
  }, [scenario?.headcount]);

  const handleBaseInputBlur = () => {
    const newBase = _.isNaN(parseFloat(base)) ? '0' : parseFloat(base);
    const newIsBaseDirty = _.isNumber(newBase) && newBase > 0;

    if (newIsBaseDirty) {
      dispatch(
        actions.setScenarioValue({
          factor: 'base',
          value: _.min([newBase, MAX_MACRO_HEADCOUNT]),
        })
      );
    } else {
      dispatch(actions.setScenarioValue({ factor: 'base', value: 0 }));
      setBase('0');
    }

    setIsBaseDirty(newIsBaseDirty);
  };

  const handleBaseInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = limitToCharLength(
      onlyAllowNumbersFromInput(event),
      MAX_MACRO_HEADCOUNT
    );
    setBase(formatWithDecimals(inputValue, 0));
  };

  const disabledInputs = window.SPWExport
    ? false
    : (scenario?.id && !isEditingSavedScenario) || currentUserIsViewOnly;

  const testingIdPrefix = 'headcount_calcs_middle_box';

  const baseInput = (
    <Grid
      item
      xs={6}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <InputWrapper>
        <Label data-testid={`${testingIdPrefix}_left_label`}>Headcount</Label>
        <NumberOp>
          <HeadcountInput
            className={`base ${
              !isBaseDirty && !disableHighlighting ? 'highlight' : ''
            }`}
            value={base}
            disabled={disabledInputs}
            onFocus={handleBaseInputFocus}
            onKeyPress={handleOnKeyPress}
            onBlur={handleBaseInputBlur}
            onChange={handleBaseInputChange}
            data-testid={`${testingIdPrefix}_left_value`}
          />
          <Operator>x</Operator>
        </NumberOp>
      </InputWrapper>
    </Grid>
  );

  if (!scenario) return null;

  return (
    <Content>
      <Paper elevation={0}>
        {(window.SPWExport || isRenderingForExport) && (
          <ScenarioHeader scenario={scenario} styles={{ marginBottom: 12 }} />
        )}
        <FormTitle style={{ minHeight: '44.5px' }}>
          CALCULATE WORK SEATS
          {calculatorActions}
        </FormTitle>
        <Container>
          <Container
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingRight: '32px',
              paddingBottom: '2px',
            }}
          >
            <Grid item xs={5}>
              <div data-testid="headcount_calcs_middle_box">
                <Headcount
                  title="HEADCOUNT UTILIZATION"
                  factorName="headcountUtilization"
                  base={scenario?.headcount}
                  baseInput={baseInput}
                  tooltip={<HeadcountUtilizationGuide />}
                  tooltipHeight={323}
                  tooltipWidth={298}
                  factorValue={scenario?.headcountUtilizationPercentage?.toString()}
                  resultValue={scenario?.headcountWithUtilization}
                  disabled={disabledInputs}
                  calculatorType={calculatorType}
                  operator="x"
                  showDecimals={showDecimals && !isEditingSavedScenario}
                  testingIdPrefix="headcount_calcs_middle_box"
                />
              </div>
            </Grid>
            <Grid item xs={1} />
            <Grid
              item
              xs={1}
              style={{ borderLeft: `1px solid ${colors.grayDivider}` }}
            />
            <Grid item xs={5}>
              <div data-testid="headcount_calcs_right_box">
                <Headcount
                  data-testid="headcount_calcs_right_box"
                  title="DESK SHARING RATIO"
                  factorName="deskSharingRatio"
                  disabled={disabledInputs}
                  base={scenario?.headcountWithUtilization}
                  rightLabel="Work Seats"
                  tooltip={<DeskSharingGuide />}
                  tooltipHeight={559}
                  tooltipWidth={298}
                  showRatio
                  factorValue={scenario?.deskSharingRatio?.toString()}
                  resultValue={scenario?.workSeats}
                  operator="÷"
                  showDecimals={showDecimals && !isEditingSavedScenario}
                  testingIdPrefix="headcount_calcs_right_box"
                />
              </div>
            </Grid>
          </Container>
        </Container>
      </Paper>
    </Content>
  );
};

export default MacroProgramming;
